import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { HeaderNav } from "@/components/HeaderNav";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const inp = "mt-1.5 w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-[#F3F4F6] focus:border-brass focus:outline-none transition-colors";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      await register({
        first_name: form.first_name.trim(), last_name: form.last_name.trim(),
        email: form.email.trim(), password: form.password,
      });
      toast.success("Account created");
      navigate("/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <HeaderNav />
      <div className="max-w-md mx-auto px-5 pt-16 pb-16 cg-fade-up">
        <p className="cg-eyebrow mb-3">Your personal account</p>
        <h1 className="font-serif text-4xl text-[#F3F4F6] mb-3">Create your account</h1>
        <p className="text-sm text-[#9CA3AF] mb-8">Your Caesar's Ghost account belongs to you, not to any organization or campaign. You can complete assessments through different groups and reassessments over time while keeping one personal account and your private portrait history.</p>
        <form onSubmit={submit} className="cg-card p-7 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#9CA3AF]">First name</label>
              <input data-testid="register-firstname-input" required value={form.first_name} onChange={set("first_name")} className={inp} />
            </div>
            <div>
              <label className="text-sm text-[#9CA3AF]">Last name</label>
              <input data-testid="register-lastname-input" required value={form.last_name} onChange={set("last_name")} className={inp} />
            </div>
          </div>
          <div>
            <label className="text-sm text-[#9CA3AF]">Email address</label>
            <input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} className={inp} />
          </div>
          <div>
            <label className="text-sm text-[#9CA3AF]">Password <span className="text-[#6B7280]">(min 8 characters)</span></label>
            <input data-testid="register-password-input" type="password" required value={form.password} onChange={set("password")} className={inp} />
          </div>
          <div>
            <label className="text-sm text-[#9CA3AF]">Confirm password</label>
            <input data-testid="register-confirm-input" type="password" required value={form.confirm} onChange={set("confirm")} className={inp} />
          </div>
          {error && <p data-testid="register-error" className="text-sm text-terracotta">{error}</p>}
          <button data-testid="register-submit-button" disabled={busy}
            className="w-full bg-brass text-obsidian font-medium py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>
        <p className="mt-5 text-sm text-[#9CA3AF]">
          Already have an account? <Link to="/login" data-testid="to-login-link" className="cg-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
